import {
  collection,
  documentId,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { endOfDay, startOfDay } from 'date-fns';

export const COMMISSIONS_PAGE_SIZE = 50;
const SALES_STATUS_CHUNK_SIZE = 30;
const COMMISSION_SUMMARY_BATCH_SIZE = 200;

export interface CommissionRecord {
  id?: string;
  staffId: string;
  staffName: string;
  saleId: string;
  saleNumber: string;
  serviceName: string;
  servicePrice: number;
  commissionRate: number;
  amount: number;
  date: any;
  status: 'pending' | 'paid' | 'voided';
  customerName?: string;
  type?: 'sale' | 'commission';
  saleStatus?: 'active' | 'voided' | 'draft';
}

export interface FetchCommissionPageParams {
  userEmail: string;
  startDate?: Date | null;
  endDate?: Date | null;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
  pageSize?: number;
}

export interface FetchCommissionPageResult {
  commissions: CommissionRecord[];
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export interface CommissionSummary {
  commissionTotal: number;
  saleSplitsTotal: number;
}

export function chunkValues<T>(values: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    throw new Error('chunkSize must be greater than 0');
  }

  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }
  return chunks;
}

export function applySaleStatuses(
  commissions: CommissionRecord[],
  salesStatusMap: Map<string, string>
): CommissionRecord[] {
  return commissions.map((commission) => {
    const saleStatus = (salesStatusMap.get(commission.saleId) || 'active') as
      | 'active'
      | 'voided'
      | 'draft';

    return {
      ...commission,
      saleStatus,
      status:
        saleStatus === 'voided' && commission.status === 'pending'
          ? 'voided'
          : commission.status,
    };
  });
}

export function summarizeCommissionTotals(commissions: CommissionRecord[]): CommissionSummary {
  return commissions.reduce(
    (summary, commission) => {
      const isVoided =
        commission.status === 'voided' || commission.saleStatus === 'voided';

      if (isVoided) {
        return summary;
      }

      if (commission.type === 'sale') {
        summary.saleSplitsTotal += commission.amount;
        return summary;
      }

      summary.commissionTotal += commission.amount;
      return summary;
    },
    { commissionTotal: 0, saleSplitsTotal: 0 }
  );
}

async function resolveStaffId(userEmail: string): Promise<string | null> {
  const firestore = getFirestore();
  const staffQuery = query(
    collection(firestore, 'staff'),
    where('email', '==', userEmail)
  );
  const staffSnapshot = await getDocs(staffQuery);

  if (staffSnapshot.empty) {
    return null;
  }

  return staffSnapshot.docs[0].id;
}

function buildCommissionQueryConstraints({
  staffId,
  startDate,
  endDate,
  cursor = null,
  pageSize,
}: {
  staffId: string;
  startDate?: Date | null;
  endDate?: Date | null;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
  pageSize?: number;
}): QueryConstraint[] {
  const constraints: QueryConstraint[] = [
    where('staffId', '==', staffId),
    orderBy('date', 'desc'),
  ];

  if (startDate) {
    constraints.push(where('date', '>=', Timestamp.fromDate(startOfDay(startDate))));
  }

  if (endDate) {
    constraints.push(where('date', '<=', Timestamp.fromDate(endOfDay(endDate))));
  }

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  if (pageSize) {
    constraints.push(limit(pageSize));
  }

  return constraints;
}

async function fetchSalesStatusMap(saleIds: string[]): Promise<Map<string, string>> {
  const firestore = getFirestore();
  const statusMap = new Map<string, string>();
  const uniqueSaleIds = Array.from(new Set(saleIds.filter(Boolean)));

  if (uniqueSaleIds.length === 0) {
    return statusMap;
  }

  for (const chunk of chunkValues(uniqueSaleIds, SALES_STATUS_CHUNK_SIZE)) {
    const salesQuery = query(
      collection(firestore, 'sales'),
      where(documentId(), 'in', chunk)
    );
    const salesSnapshot = await getDocs(salesQuery);

    salesSnapshot.docs.forEach((saleDoc) => {
      statusMap.set(saleDoc.id, saleDoc.data().status || 'active');
    });
  }

  return statusMap;
}

async function enrichCommissionsWithSaleStatuses(
  rawCommissions: CommissionRecord[]
): Promise<CommissionRecord[]> {
  const salesStatusMap = await fetchSalesStatusMap(
    rawCommissions.map((commission) => commission.saleId)
  );

  return applySaleStatuses(rawCommissions, salesStatusMap);
}

export async function fetchCommissionSummary({
  userEmail,
  startDate,
  endDate,
}: Omit<FetchCommissionPageParams, 'cursor' | 'pageSize'>): Promise<CommissionSummary> {
  const firestore = getFirestore();
  const staffId = await resolveStaffId(userEmail);

  if (!staffId) {
    return { commissionTotal: 0, saleSplitsTotal: 0 };
  }

  let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
  let summary: CommissionSummary = { commissionTotal: 0, saleSplitsTotal: 0 };

  while (true) {
    const constraints = buildCommissionQueryConstraints({
      staffId,
      startDate,
      endDate,
      cursor,
      pageSize: COMMISSION_SUMMARY_BATCH_SIZE,
    });

    const commissionsQuery = query(collection(firestore, 'commissions'), ...constraints);
    const commissionSnapshot = await getDocs(commissionsQuery);

    if (commissionSnapshot.empty) {
      break;
    }

    const rawCommissions = commissionSnapshot.docs.map((commissionDoc) => ({
      id: commissionDoc.id,
      ...(commissionDoc.data() as Omit<CommissionRecord, 'id'>),
    }));

    const commissions = await enrichCommissionsWithSaleStatuses(rawCommissions);
    const batchSummary = summarizeCommissionTotals(commissions);

    summary = {
      commissionTotal: summary.commissionTotal + batchSummary.commissionTotal,
      saleSplitsTotal: summary.saleSplitsTotal + batchSummary.saleSplitsTotal,
    };

    if (commissionSnapshot.docs.length < COMMISSION_SUMMARY_BATCH_SIZE) {
      break;
    }

    cursor = commissionSnapshot.docs.at(-1) ?? null;

    if (!cursor) {
      break;
    }
  }

  return summary;
}

export async function fetchCommissionPage({
  userEmail,
  startDate,
  endDate,
  cursor = null,
  pageSize = COMMISSIONS_PAGE_SIZE,
}: FetchCommissionPageParams): Promise<FetchCommissionPageResult> {
  const firestore = getFirestore();
  const staffId = await resolveStaffId(userEmail);

  if (!staffId) {
    return { commissions: [], lastVisible: null, hasMore: false };
  }

  const constraints = buildCommissionQueryConstraints({
    staffId,
    startDate,
    endDate,
    cursor,
    pageSize,
  });

  const commissionsQuery = query(collection(firestore, 'commissions'), ...constraints);
  const commissionSnapshot = await getDocs(commissionsQuery);
  const rawCommissions = commissionSnapshot.docs.map((commissionDoc) => ({
    id: commissionDoc.id,
    ...(commissionDoc.data() as Omit<CommissionRecord, 'id'>),
  }));

  const commissions = await enrichCommissionsWithSaleStatuses(rawCommissions);
  const lastVisible = commissionSnapshot.docs.at(-1) ?? null;

  return {
    commissions,
    lastVisible,
    hasMore: commissionSnapshot.docs.length === pageSize,
  };
}
